/**
 * DocContext — 文档级核心数据 + 回调上下文。
 *
 * 所有块组件都从此 context 读取 definitions、registry 及各种文档级回调。
 * outputs 独立放在 DocOutputsContext，避免大输出变更带着 MarkdownBlock 等非输出
 * 消费者一起 re-render。
 */

import type {
    ContentDefinition,
    ForBlock,
    ImportBinding,
    ImportDocumentSnapshot,
    PluginDefinition,
    PluginOutput,
    UseBinding,
} from "@magic-md/core";
import type { ActionResult, ApiResult, RendererConfigChangeOptions } from "@magic-md/plugin-sdk";
import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import type { MarkdownLinkOpenTarget, PluginRegistryLike } from "../types";

/* ── Value ── */

export interface DocContextValue {
    // ── 核心数据 ──
    definitions: Map<string, PluginDefinition>;
    outputs?: Map<string, PluginOutput>;
    registry: PluginRegistryLike;
    executable: boolean;
    documentPath?: string | null;
    sourceContent?: string;
    getSourceContent?: () => string | undefined;
    documentBundlePath?: string | null;
    /**
     * 是否处于"编辑模式" — 透传给插件 Renderer 的 `editMode` 属性。
     * 默认为 `false`；调用方（PreviewPanel / 内联编辑器等）可在 Provider 上覆盖，
     * 以便插件统一感知，无需逐个 callsite 改造。
     */
    editMode?: boolean;
    contentDefinitions?: Map<string, ContentDefinition>;
    forBlocksMap?: Map<string, ForBlock>;
    importBindings?: readonly ImportBinding[];
    useBindings?: readonly UseBinding[];
    importDocuments?: Map<string, ImportDocumentSnapshot>;
    /**
     * Unit IDs authored in the current document before imported symbols are
     * hydrated into the render context. Used to distinguish a local `ns/id`
     * definition from an imported public symbol with the same namespace shape.
     */
    localExecutionUnitIds?: ReadonlySet<string>;
    /**
     * Namespace of the import unit that owns this rendered document snapshot.
     * Nested import-document refreshes delegate to this owner when they are
     * rendered from a parent session.
     */
    importRenderOwnerNamespace?: string;

    // ── 回调 ──
    onAction?: (defId: string, actionName: string, params?: Record<string, unknown>) => ActionResult | void | Promise<ActionResult | void>;
    onApi?: <TData = unknown>(defId: string, apiName: string, params?: Record<string, unknown>) => ApiResult<TData> | Promise<ApiResult<TData>>;
    onDefinitionConfigChange?: (defId: string, nextConfig: Record<string, unknown>, options?: RendererConfigChangeOptions) => boolean | void | Promise<boolean | void>;
    onRun?: (defId: string) => void;
    onPin?: (defId: string) => void;
    onDefinitionSelect?: (defId: string) => void;
    onDebug?: (defId: string) => void;
    onRunFor?: (forId: string) => void;
    onCancelFor?: (forId: string) => void;
    onRunContent?: (contentId: string) => void;
    onRunImport?: (namespace: string) => void;
    onRunUse?: (useId: string) => void;
    onLoadOutput?: (definitionId: string) => Promise<void>;

    // ── 插件能力回调 ──
    callSidecar?: (sidecarId: string, method: string, params?: Record<string, unknown>) => Promise<{ success: boolean; result?: unknown; error?: string }>;
    onEngineNotification?: <T = unknown>(method: string, handler: (params: T) => void) => () => void;
    selectFile?: (options?: { accept?: string[]; title?: string }) => Promise<string | null>;
    selectFolder?: (options?: { title?: string }) => Promise<string | null>;
    openFile?: (filePath: string, target?: MarkdownLinkOpenTarget) => Promise<void>;
    openExternal?: (url: string) => Promise<void>;
    getAssetUrl?: (filePath: string) => string | undefined;
    getDefinitions?: () => Array<{ id: string; pluginName: string; hasOutput: boolean }>;
    getTabs?: () => Array<{ id: string; title: string; filePath?: string }>;

    // ── UI 注入 ──
    ContentBlockWrapper?: React.ComponentType<{ definitionId: string; children: React.ReactNode }>;
}

export interface DocOutputsStore {
    getOutput: (definitionId: string) => PluginOutput | undefined;
    subscribeOutput?: (
        definitionId: string,
        onStoreChange: () => void,
    ) => () => void;
    getOutputs?: () => Map<string, PluginOutput>;
}

/* ── Context ── */

const DocContext = createContext<DocContextValue | null>(null);
const DocOutputsContext = createContext<Map<string, PluginOutput> | DocOutputsStore | null>(null);
const EMPTY_OUTPUTS = new Map<string, PluginOutput>();

export const DocContextProvider = DocContext.Provider;
export const DocOutputsProvider = DocOutputsContext.Provider;

/* ── Hook ── */

export function useDocContext(): DocContextValue {
    const ctx = useContext(DocContext);
    if (!ctx) {
        throw new Error("useDocContext() must be used within a <DocContextProvider>.");
    }
    return ctx;
}

export function useDocOutputs(): Map<string, PluginOutput> {
    const outputs = useContext(DocOutputsContext);
    const doc = useContext(DocContext);
    if (outputs instanceof Map) return outputs;
    if (outputs?.getOutputs) return outputs.getOutputs();
    return doc?.outputs ?? EMPTY_OUTPUTS;
}

export function useDocOutput(definitionId: string): PluginOutput | undefined {
    const outputs = useContext(DocOutputsContext);
    const doc = useContext(DocContext);
    const outputStore = outputs instanceof Map ? null : outputs;
    const outputMap = outputs instanceof Map ? outputs : null;
    const fallbackOutputs = doc?.outputs;

    const subscribe = useCallback(
        (onStoreChange: () => void) =>
            outputStore?.subscribeOutput
                ? outputStore.subscribeOutput(definitionId, onStoreChange)
                : () => { },
        [definitionId, outputStore],
    );
    const getSnapshot = useCallback(
        () => {
            if (outputStore) return outputStore.getOutput(definitionId);
            return outputMap?.get(definitionId) ?? fallbackOutputs?.get(definitionId);
        },
        [definitionId, fallbackOutputs, outputMap, outputStore],
    );

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
